import { capitalizeTerm, type ForgeBoxSort } from "@loot-os/api-contract";
import { BoxCard } from "@loot-os/ui/components/box-card";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { useCurrency } from "../lib/currency.js";
import { forgeBoxesInfiniteQueryOptions } from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";
import { useLoadMore } from "../lib/useLoadMore.js";

const SKELETON_KEYS = ["a", "b", "c", "d"];

/** GET /v1/forge/boxes: the boxes shoppers built, newest or most opened first. */
export function ForgePage() {
  const { user } = useShopper();
  const { box, item, forge } = useTerminology();
  const currency = useCurrency();
  const [sort, setSort] = useState<ForgeBoxSort>("new");
  const query = useInfiniteQuery(forgeBoxesInfiniteQueryOptions(sort));
  const boxes = query.data?.pages.flatMap((page) => page.data) ?? [];
  const sentinel = useLoadMore<HTMLDivElement>(
    !!query.hasNextPage && !query.isFetchingNextPage,
    () => void query.fetchNextPage(),
  );

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {capitalizeTerm(forge.singular)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {capitalizeTerm(box.plural)} built by players: pick the {item.plural}, set the odds, and
            earn when others open them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <Link to="/forge/mine" className={buttonVariants({ variant: "secondary" })}>
              My {box.plural}
            </Link>
          )}
          {user ? (
            <Link to="/forge/create" className={buttonVariants()}>
              Create {box.singular}
            </Link>
          ) : (
            <Link to="/login" className={buttonVariants({ variant: "secondary" })}>
              Sign in to forge
            </Link>
          )}
        </div>
      </div>

      <Tabs
        value={sort}
        onValueChange={(value) => setSort(value === "popular" ? "popular" : "new")}
      >
        <TabsList>
          <TabsTrigger value="new">Newest</TabsTrigger>
          <TabsTrigger value="popular">Most opened</TabsTrigger>
        </TabsList>
      </Tabs>

      {query.isError && (
        <ErrorBanner>
          {query.error instanceof Error ? query.error.message : `failed to load ${box.plural}`}
        </ErrorBanner>
      )}

      {query.isPending ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {SKELETON_KEYS.map((id) => (
            <Skeleton key={id} className="aspect-4/5 rounded-xl" />
          ))}
        </div>
      ) : boxes.length === 0 && !query.isError ? (
        <p className="text-sm text-muted-foreground">
          Nothing forged yet. Be the first to build one.
        </p>
      ) : (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {boxes.map((forged) => (
              <Link
                key={forged.id}
                to="/boxes/$boxId"
                params={{ boxId: forged.id }}
                className="grid gap-1.5"
              >
                <BoxCard box={forged} currency={currency} />
                <span className="truncate px-1 text-xs text-muted-foreground">
                  Forged by{" "}
                  <span className="text-foreground">{forged.forge.creatorDisplayName}</span>
                </span>
              </Link>
            ))}
          </div>
          {query.hasNextPage ? (
            <div ref={sentinel} className="flex justify-center">
              <Button
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
